import type { FC } from 'react';

import { ButtonBase, CircularProgress } from '@mui/material';
import { QuestionIcon } from '@truvhq/ui';

import { useAssistantEnabled } from 'features/onboarding/lib/useAssistantEnabled';
import { useChecklistProgress } from 'features/onboarding/lib/useOnboardingChecklist';
import { useOnboardingDialogStore } from 'features/onboarding/lib/useOnboardingDialogStore';
import { useScreenSize } from 'shared/lib/screen/useScreenSize';

import RocketIcon from './icons/rocket.svg?react';

type Props = {
  /** For a page with no nav to reach the assistant from — see below */
  keepOnMobile?: boolean;
};

/**
 * Floating launcher for the assistant, in the bottom-right corner. Below the dialog's z-index so it
 * tucks behind the panel once opened.
 *
 * Gated out here rather than by the caller, as OnboardingDialog is: the checklist hook below fetches
 * without an `enabled` of its own, so a body that bailed on its last line would still have made the
 * request. Both mount at the app root, because a page is no less in need of the assistant for being
 * full-screen — the create-order and template pages use a layout of their own and had no way in.
 *
 * Desktop chrome: on a phone a floating pill lands on top of whatever the page pins to its own
 * bottom edge, so the assistant is reached from the nav drawer instead (CL-6904). The verification
 * page is the exception — it has no drawer, and nothing pinned under the form for the pill to cover.
 */
export const AssistantLauncherButton: FC<Props> = ({ keepOnMobile = false }) => {
  const { isMobile } = useScreenSize();

  return useAssistantEnabled() && (keepOnMobile || !isMobile) ? <AssistantLauncher /> : null;
};

const AssistantLauncher: FC = () => {
  const open = useOnboardingDialogStore((state) => state.open);
  // While setup is unfinished the launcher says how far along it is and leads with the rocket; it
  // only offers the assistant in the abstract once there is nothing left to point at. Until the
  // checklist resolves it says neither: on a cold open it used to read "Ask Truv AI" for a beat and
  // then rename itself to the setup step, which looks like the button changed its mind.
  const { progress, isPending } = useChecklistProgress();

  return (
    <ButtonBase
      onClick={open}
      // Deliberately still clickable: this is the only way into support, and a slow checklist
      // request is no reason to lock someone out of it.
      aria-busy={isPending}
      sx={{
        position: 'fixed',
        right: 24,
        bottom: 'max(22px, env(safe-area-inset-bottom))',
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        gap: '9px',
        maxWidth: 'calc(100vw - 3rem)',
        padding: '11px 19px',
        borderRadius: 9999,
        border: '1px solid',
        borderColor: 'grey.300',
        backgroundColor: 'common.white',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.12)',
        color: 'text.primary',
        font: '500 14px/20px system-ui',
        '&:hover': { borderColor: 'primary.main', backgroundColor: 'common.white' },
      }}
    >
      {isPending ? (
        <CircularProgress size={18} color="inherit" aria-hidden />
      ) : progress ? (
        <RocketIcon width={18} height={18} aria-hidden />
      ) : (
        <QuestionIcon width={18} height={18} aria-hidden />
      )}
      <span className="truncate">
        {isPending ? 'Loading' : progress ? `Setup: step ${progress.position} of ${progress.total}` : 'Ask Truv AI'}
      </span>
    </ButtonBase>
  );
};
